import type {
  BorderEdge,
  ImageAsset,
  ImageComponent,
  LeafPrimitive,
  Placement,
  RegionBorders,
  SymbolComponent,
  TextComponent,
  VariableMeta,
  ZoneStyle,
} from '@tsagko/label-contracts'
import { canonicalStructureKind, DEFAULT_HRI_SIZE_PT, RENDERABLE_SYMBOLOGIES, SYMBOLOGY_METRICS, type RenderableSymbology } from '@tsagko/label-contracts'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdapter } from '../adapters/AdapterProvider'
import { analyzeImageSource } from '../adapters/demo/resolve-images'
import { BorderPicker } from '../canvas/BorderPicker'
import type { ImageAnalysis } from '../canvas/rasterize-image'
import { ZoneDimensionsField } from '../canvas/ZoneDimensionsField'
import { CommitInput, CommitTextarea } from '../ui/CommitField'
import { useConfirm } from '../ui/ConfirmDialog'
import { AlignmentGrid } from '../template-designer/AlignmentGrid'
import { SymbolSourceEditor } from './SymbolSourceEditor'
import { SYMBOLOGY_OPTIONS } from './symbol-config'
import { variableNamesForLeaf } from './region-derivation'
import { DEFAULT_TEXT_SIZE_PT } from './text-defaults'
import { isMixedBraceText } from './variables-ops'

interface RegionPropertyPanelProps {
  selectedZoneId?: string
  selected?: LeafPrimitive
  selectedZoneStyle?: ZoneStyle
  selectedBorders?: RegionBorders
  /** Selected zone's current laid-out width/height (mm), for the region-size editor (issue #126). */
  selectedZoneWidthMm?: number
  selectedZoneHeightMm?: number
  variables: Record<string, VariableMeta>
  onChangePrimitive: (next: LeafPrimitive) => void
  onChangeZoneStyle: (next: ZoneStyle | undefined) => void
  onChangeVariableMeta: (name: string, meta: VariableMeta) => void
  onChangePlacement: (next: Placement | undefined) => void
  onSetEdge: (edge: keyof RegionBorders, value: BorderEdge | undefined) => void
  /** Commits a region-size (mm) edit — converted to a split ratio by the caller. */
  onZoneDimensionsChange?: (next: { widthMm: number; heightMm: number }) => void
  /**
   * Renders the region-size field read-only (mirrors TemplatePropertyPanel's
   * `disabled={dimensionsLocked === true && path.length === 0}`). The caller
   * computes this — e.g. the block-configurator passes true only when the
   * dimension lock is on AND the selected zone is the block's root, since a
   * root-zone edit resizes the block itself while a split child's edit only
   * adjusts the parent ratio and stays live. Passing `onZoneDimensionsChange`
   * alone is NOT enough to gate the field: ZoneDimensionsField's own draft
   * input only resyncs to the real value on a `valueMm` prop change, so a
   * write the caller silently no-ops would leave the input showing the
   * user's typed (never-applied) value indefinitely.
   */
  regionSizeDisabled?: boolean
}

function blankText(): TextComponent {
  return { type: 'text', id: `c-${Date.now()}`, value: { content: '' } }
}

function blankSymbol(): SymbolComponent {
  return {
    type: 'symbol',
    id: `c-${Date.now()}`,
    symbology: 'code-128',
    dataStructure: { kind: 'raw', value: { content: '' } },
  }
}

function blankImage(): ImageComponent {
  return { type: 'image', id: `c-${Date.now()}`, source: { kind: 'data-uri', value: '' }, fit: 'contain' }
}

/**
 * Returns `symbol` with its dataStructure snapped to the canonical kind for its
 * symbology. Preserves any single value string when moving between single-value
 * kinds (raw/gtin/url); GS1 starts with an empty element list. Idempotent.
 */
export function canonicalizeSymbol(symbol: SymbolComponent): SymbolComponent {
  if (!(RENDERABLE_SYMBOLOGIES as readonly string[]).includes(symbol.symbology)) {
    return symbol // non-renderable legacy symbology — leave unchanged
  }
  const target = canonicalStructureKind(symbol.symbology as RenderableSymbology)
  const ds = symbol.dataStructure
  if (ds.kind === target) return symbol
  if (target === 'gs1-ai') {
    return { ...symbol, dataStructure: { kind: 'gs1-ai', elements: [] } }
  }
  const value = 'value' in ds ? ds.value : { content: '' }
  if (target === 'gtin') return { ...symbol, dataStructure: { kind: 'gtin', value } }
  return { ...symbol, dataStructure: { kind: 'raw', value } }
}

/**
 * Returns true when a primitive has content worth preserving (would be lost on type switch).
 * - text: non-empty content
 * - symbol: non-empty value OR non-empty elements
 * - image: any non-empty source
 */
function isPrimitiveConfigured(p: LeafPrimitive): boolean {
  if (p.type === 'text') return p.value.content.trim() !== ''
  if (p.type === 'symbol') {
    const ds = p.dataStructure
    if (ds.kind === 'gs1-ai') return ds.elements.length > 0
    return ds.value.content.trim() !== ''
  }
  if (p.type === 'image') {
    if (p.source.kind === 'data-uri') return p.source.value !== ''
    if (p.source.kind === 'url') return p.source.url !== ''
    if (p.source.kind === 'host-asset') return p.source.assetId !== ''
    return false
  }
  return false
}

/**
 * Returns true when re-canonicalizing `symbol` for `toSymbology` would lose
 * configured data. Derived from the canonical STRUCTURE KINDS (not hardcoded
 * symbology names) so the confirm stays honest if a future symbology maps to
 * gs1-ai. MUST stay in lockstep with what canonicalizeSymbol preserves:
 *   - single-value kinds (raw/gtin/url) carry the value string between each other
 *   - moving TO 'gs1-ai' drops any single value (elements start empty)
 *   - moving FROM 'gs1-ai' drops the element list (no single value carries over)
 */
function isSymbologyDestructive(
  symbol: SymbolComponent,
  toSymbology: SymbolComponent['symbology']
): boolean {
  if (!(RENDERABLE_SYMBOLOGIES as readonly string[]).includes(toSymbology)) {
    return false // canonicalizeSymbol leaves non-renderable symbologies unchanged
  }
  const target = canonicalStructureKind(toSymbology as RenderableSymbology)
  const ds = symbol.dataStructure
  if (ds.kind === target) return false // structure unchanged → nothing lost
  if (target === 'gs1-ai') {
    // single value → empty element list: lost iff a value exists
    return 'value' in ds && ds.value.content.trim() !== ''
  }
  if (ds.kind === 'gs1-ai') {
    // element list → single value (starts empty): lost iff elements exist
    return ds.elements.length > 0
  }
  // single-value → single-value (raw ↔ gtin ↔ url): value carries over
  return false
}

// AlignmentGrid uses the baked-block-placement convention: vAlign = 'top' | 'center' | 'bottom'.
// The new PlacementSchema uses vAlign = 'top' | 'middle' | 'bottom'. Adapt at the boundary so
// neither AlignmentGrid nor the schema needs to change.
type GridVAlign = 'top' | 'center' | 'bottom'

function placementVToGridV(v: Placement['vAlign'] | undefined): GridVAlign {
  if (v === 'middle') return 'center'
  if (v === 'top' || v === 'bottom') return v
  // Mirror pipeline DEFAULT_PLACEMENT (vAlign='top') so the picker reflects
  // where the canvas actually anchors an unset-placement primitive.
  return 'top'
}

// AlignmentGrid's hAlign is the same 3-way baked-block convention; the schema's
// 'justify' (added for the alignment-fold migration — see PlacementSchema's
// docstring in packages/contracts) has no cell in the 3×3 picker. Fold it to
// 'left' for display purposes only: a 'justify' value is reachable via
// migration/programmatic API, never via this grid, so this adapter never
// needs to round-trip 'justify' back out (gridHToPlacementH doesn't exist —
// the grid can only ever emit its own 3 values).
type GridHAlign = 'left' | 'center' | 'right'

function placementHToGridH(h: Placement['hAlign'] | undefined): GridHAlign {
  if (h === 'left' || h === 'center' || h === 'right') return h
  return 'left'
}

function gridVToPlacementV(v: GridVAlign): Placement['vAlign'] {
  if (v === 'center') return 'middle'
  return v
}

export function RegionPropertyPanel({
  selectedZoneId,
  selected,
  selectedZoneStyle,
  selectedBorders,
  selectedZoneWidthMm,
  selectedZoneHeightMm,
  variables,
  onChangePrimitive,
  onChangeZoneStyle,
  onChangeVariableMeta,
  onChangePlacement,
  onSetEdge,
  onZoneDimensionsChange,
  regionSizeDisabled,
}: RegionPropertyPanelProps) {
  // ALL hooks must run unconditionally before the early-return guard to avoid
  // the React "rendered more/fewer hooks" error when selected toggles.

  const { t } = useTranslation()
  const confirm = useConfirm()

  const referencedVars = useMemo<string[]>(
    () => (selected ? variableNamesForLeaf(selected) : []),
    [selected]
  )

  // Normalize a legacy symbol (e.g. ean-13 stored as kind:'raw') to its
  // canonical structure when it's opened. Runs when the selected component
  // identity changes.
  useEffect(() => {
    if (selected?.type !== 'symbol') return
    const normalized = canonicalizeSymbol(selected)
    if (normalized !== selected) onChangePrimitive(normalized)
    // Snap the structure once when the zone is first opened — not on every
    // keystroke — so `selected`/`onChangePrimitive` are intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZoneId])

  if (!selected || !selectedZoneId) {
    return (
      <aside
        data-testid="region-property-panel"
        className="flex w-72 shrink-0 flex-col border-l border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900"
      >
        <p className="text-xs text-stone-400">{t('blockConfigurator.regionPanel.emptyHint')}</p>
      </aside>
    )
  }

  const currentPlacement: Placement | undefined =
    'placement' in selected ? selected.placement : undefined

  return (
    <aside
      data-testid="region-property-panel"
      className="flex w-72 shrink-0 flex-col overflow-auto border-l border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900"
    >
      {/* Section: Region size (mm) — issue #126. Converts to the parent
          split's ratio at commit time (ratioForChildSize/clampSplitRatio);
          the ratio-based Zone/Split schema itself doesn't change. */}
      <section className="mb-4">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.regionPanel.regionSize')}
        </label>
        <div className="mt-1">
          <ZoneDimensionsField
            widthMm={selectedZoneWidthMm}
            heightMm={selectedZoneHeightMm}
            disabled={regionSizeDisabled}
            onChange={onZoneDimensionsChange}
          />
        </div>
      </section>

      {/* Section: Primitive type picker */}
      <section className="mb-4">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.regionPanel.primitiveType')}
        </label>
        <select
          data-testid="rpp-primitive-type"
          value={selected.type}
          onChange={e => {
            const next = e.target.value
            void (async () => {
              if (next === selected.type) return
              if (
                isPrimitiveConfigured(selected) &&
                !(await confirm({
                  title: t('confirm.switchPrimitiveType.title'),
                  body: t('confirm.switchPrimitiveType.body'),
                  danger: true,
                }))
              ) {
                return
              }
              if (next === 'text') onChangePrimitive(blankText())
              else if (next === 'symbol') onChangePrimitive(blankSymbol())
              else if (next === 'image') onChangePrimitive(blankImage())
            })()
          }}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
        >
          <option value="text">{t('blockConfigurator.regionPanel.type.text')}</option>
          <option value="symbol">{t('blockConfigurator.regionPanel.type.symbol')}</option>
          <option value="image">{t('blockConfigurator.regionPanel.type.image')}</option>
        </select>
      </section>

      {/* Section: Source */}
      <section className="mb-4">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.regionPanel.source')}
        </label>
        {selected.type === 'text' && (
          <>
            <CommitTextarea
              data-testid="rpp-text-source"
              value={selected.value.content}
              onCommit={content =>
                onChangePrimitive({
                  ...selected,
                  value: { content },
                })
              }
              placeholder={t('blockConfigurator.regionPanel.textPlaceholder')}
              rows={2}
              className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 font-mono text-sm dark:border-stone-700 dark:bg-stone-950"
            />
            {isMixedBraceText(selected.value.content) && (
              <p
                data-testid="rpp-mixed-variable-note"
                className="mt-1 text-[11px] text-amber-600 dark:text-amber-400"
              >
                {t('blockConfigurator.regionPanel.mixedBraceNote')}
              </p>
            )}
          </>
        )}
        {selected.type === 'symbol' && (
          <SymbolSourceEditor selected={selected} onChangePrimitive={onChangePrimitive} />
        )}
        {selected.type === 'image' && (
          <ImageSourceControls selected={selected} onChangePrimitive={onChangePrimitive} />
        )}
      </section>

      {/* Section: Variable metadata — one block per referenced variable */}
      {referencedVars.map(name => {
        const meta = variables[name]
        if (!meta) return null
        return (
          <section
            key={name}
            data-testid="rpp-variable-meta"
            className="mb-4 rounded border border-fuchsia-300 bg-fuchsia-50 p-3 dark:border-fuchsia-700 dark:bg-fuchsia-950"
          >
            <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-fuchsia-800 dark:text-fuchsia-200">
              {t('blockConfigurator.regionPanel.variable.heading', { name })}
            </h4>
            <label className="block text-xs">
              <span className="text-stone-500">
                {t('blockConfigurator.regionPanel.variable.type')}
              </span>
              <select
                data-testid="rpp-variable-type"
                value={meta.type}
                onChange={e =>
                  onChangeVariableMeta(name, {
                    ...meta,
                    type: e.target.value as VariableMeta['type'],
                  })
                }
                className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
              >
                <option value="text">{t('blockConfigurator.regionPanel.variable.typeText')}</option>
                <option value="number">{t('blockConfigurator.regionPanel.variable.typeNumber')}</option>
                <option value="date">{t('blockConfigurator.regionPanel.variable.typeDate')}</option>
                <option value="boolean">{t('blockConfigurator.regionPanel.variable.typeBoolean')}</option>
                <option value="enum">{t('blockConfigurator.regionPanel.variable.typeEnum')}</option>
              </select>
            </label>
            <label className="mt-2 block text-xs">
              <span className="text-stone-500">
                {t('blockConfigurator.regionPanel.variable.default')}
              </span>
              {meta.type === 'text' && (
                <CommitInput
                  data-testid="rpp-variable-default"
                  value={meta.default ?? ''}
                  onCommit={v =>
                    onChangeVariableMeta(name, { ...meta, default: v })
                  }
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                />
              )}
              {meta.type === 'boolean' && (
                <select
                  data-testid="rpp-variable-default"
                  value={meta.default ?? ''}
                  onChange={e =>
                    onChangeVariableMeta(name, { ...meta, default: e.target.value })
                  }
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                >
                  <option value="">{t('blockConfigurator.regionPanel.variable.none')}</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              )}
              {meta.type === 'enum' && (
                <select
                  data-testid="rpp-variable-default"
                  value={meta.default ?? ''}
                  onChange={e =>
                    onChangeVariableMeta(name, { ...meta, default: e.target.value })
                  }
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                >
                  <option value="">{t('blockConfigurator.regionPanel.variable.none')}</option>
                  {(meta.enumOptions ?? []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
              {meta.type === 'number' && (
                <CommitInput
                  data-testid="rpp-variable-default"
                  type="number"
                  value={meta.default ?? ''}
                  onCommit={v => {
                    if (v === '') {
                      onChangeVariableMeta(name, { ...meta, default: '' })
                      return
                    }
                    const n = Number(v)
                    if (Number.isNaN(n)) return // invalid — don't write
                    const decimals = meta.format?.kind === 'number' ? meta.format.decimals : undefined
                    const formatted = decimals !== undefined ? n.toFixed(decimals) : String(n)
                    onChangeVariableMeta(name, { ...meta, default: formatted })
                  }}
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                />
              )}
              {meta.type === 'date' && (
                // Date default stored as yyyy-mm-dd (ISO date string).
                // resolveContent → applyFormat → formatDate parses via new Date(value)
                // which accepts ISO format, then reformats per the declared pattern at print time.
                // The stored value is NOT printed verbatim — it's always reformatted.
                <input
                  data-testid="rpp-variable-default"
                  type="date"
                  value={meta.default ?? ''}
                  onChange={e =>
                    onChangeVariableMeta(name, { ...meta, default: e.target.value })
                  }
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                />
              )}
            </label>
            {meta.type === 'date' && (
              <label className="mt-2 block text-xs">
                <span className="text-stone-500">
                  {t('blockConfigurator.regionPanel.variable.datePattern')}
                </span>
                <input
                  data-testid="rpp-variable-date-pattern"
                  value={meta.format?.kind === 'date' ? meta.format.pattern : ''}
                  onChange={e =>
                    onChangeVariableMeta(name, {
                      ...meta,
                      format: { kind: 'date', pattern: e.target.value },
                    })
                  }
                  placeholder="DD-MM-YYYY"
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                />
              </label>
            )}
            {meta.type === 'number' && (
              <label className="mt-2 block text-xs">
                <span className="text-stone-500">
                  {t('blockConfigurator.regionPanel.variable.decimals')}
                </span>
                <input
                  data-testid="rpp-variable-number-decimals"
                  type="number"
                  value={
                    meta.format?.kind === 'number'
                      ? meta.format.decimals ?? ''
                      : ''
                  }
                  onChange={e =>
                    onChangeVariableMeta(name, {
                      ...meta,
                      format: {
                        kind: 'number',
                        decimals:
                          e.target.value === '' ? undefined : Number(e.target.value),
                      },
                    })
                  }
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                />
              </label>
            )}
            {meta.type === 'enum' && (
              <label className="mt-2 block text-xs">
                <span className="text-stone-500">
                  {t('blockConfigurator.regionPanel.variable.enumOptions')}
                </span>
                <input
                  data-testid="rpp-variable-enum-options"
                  value={(meta.enumOptions ?? []).join(',')}
                  onChange={e =>
                    onChangeVariableMeta(name, {
                      ...meta,
                      enumOptions: e.target.value
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="kg,g,lb"
                  className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
                />
              </label>
            )}
          </section>
        )
      })}

      {/* Section: Region styling. Hidden for symbol regions — a barcode on a
          black background is unscannable (issues #4 + #7d). A LEGACY doc that
          already carries a dark background gets a warning + one-click removal
          instead (never silently mutated). */}
      {selected.type !== 'symbol' && (
        <section className="mb-4">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            {t('blockConfigurator.regionPanel.background')}
          </label>
          <select
            data-testid="rpp-background"
            value={selectedZoneStyle?.backgroundColor ?? 'none'}
            onChange={e => {
              const next = e.target.value
              if (next === 'none') onChangeZoneStyle(undefined)
              else onChangeZoneStyle({ backgroundColor: next })
            }}
            className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
          >
            <option value="none">{t('blockConfigurator.regionPanel.backgroundNone')}</option>
            <option value="black">{t('blockConfigurator.regionPanel.backgroundBlack')}</option>
          </select>
        </section>
      )}
      {selected.type === 'symbol' && selectedZoneStyle?.backgroundColor === 'black' && (
        <section className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950">
          <p
            data-testid="rpp-barcode-bg-warning"
            className="text-[11px] text-amber-700 dark:text-amber-300"
          >
            {t('blockConfigurator.regionPanel.barcodeBgWarning')}
          </p>
          <button
            type="button"
            data-testid="rpp-barcode-bg-remove"
            onClick={() => onChangeZoneStyle(undefined)}
            className="mt-2 w-full rounded border border-amber-400 px-2 py-1 text-xs text-amber-800 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-200 dark:hover:bg-amber-900"
          >
            {t('blockConfigurator.regionPanel.removeBackground')}
          </button>
        </section>
      )}

      <BorderPicker borders={selectedBorders} onSetEdge={onSetEdge} />

      {/* Section: Placement */}
      <section className="mb-4">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {t('blockConfigurator.regionPanel.placement')}
        </label>
        <div className="mt-1">
          <AlignmentGrid
            hAlign={placementHToGridH(currentPlacement?.hAlign)}
            vAlign={placementVToGridV(currentPlacement?.vAlign)}
            onChange={p =>
              onChangePlacement({
                hAlign: p.hAlign,
                vAlign: gridVToPlacementV(p.vAlign),
              })
            }
          />
        </div>
      </section>

      {/* Section: Primitive-specific (text font size, symbol symbology, etc.) */}
      {selected.type === 'text' && (
        <section className="mb-4">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            {t('blockConfigurator.regionPanel.fontSize')}
          </label>
          <FontSizeInput selected={selected} onChangePrimitive={onChangePrimitive} />
        </section>
      )}
      {selected.type === 'symbol' && (
        <section className="mb-4">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            {t('blockConfigurator.regionPanel.symbology')}
          </label>
          <select
            data-testid="rpp-symbol-symbology"
            value={selected.symbology}
            onChange={e => {
              const nextSymbology = e.target.value as SymbolComponent['symbology']
              // The spread below uses `selected` captured at change time; the
              // confirm dialog's modal overlay blocks any mid-confirm re-edit,
              // so the captured value cannot go stale while we await.
              void (async () => {
                if (
                  isSymbologyDestructive(selected, nextSymbology) &&
                  !(await confirm({
                    title: t('confirm.switchSymbology.title'),
                    body: t('confirm.switchSymbology.body'),
                    danger: true,
                  }))
                ) {
                  return
                }
                onChangePrimitive(
                  canonicalizeSymbol({ ...selected, symbology: nextSymbology })
                )
              })()
            }}
            className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
          >
            {!SYMBOLOGY_OPTIONS.some(o => o.value === selected.symbology) && (
              <option value={selected.symbology} disabled>
                {t('blockConfigurator.regionPanel.symbologyUnsupported', {
                  symbology: selected.symbology,
                })}
              </option>
            )}
            {SYMBOLOGY_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </section>
      )}
      {selected.type === 'symbol' && (
        <BarcodeSizeSection selected={selected} onChangePrimitive={onChangePrimitive} />
      )}
      {selected.type === 'symbol' && selected.symbology !== 'qr' && (
        <HumanReadableSection selected={selected} onChangePrimitive={onChangePrimitive} />
      )}
      {selected.type === 'text' && (
        <TextRotationSection selected={selected} onChangePrimitive={onChangePrimitive} />
      )}

      {/* Section: Clear region — replaces the component with the canonical
          empty-text placeholder (the Zone itself, incl. style/borders, stays). */}
      <section className="mt-2">
        <button
          type="button"
          data-testid="rpp-clear-region"
          disabled={!isPrimitiveConfigured(selected)}
          title={
            isPrimitiveConfigured(selected)
              ? undefined
              : t('blockConfigurator.regionPanel.alreadyEmpty')
          }
          onClick={() => {
            // `selected` captured at click time is safe: the confirm dialog's
            // modal overlay blocks any mid-confirm re-edit, so it cannot go
            // stale while we await (same contract as the symbology handler).
            void (async () => {
              if (
                isPrimitiveConfigured(selected) &&
                !(await confirm({
                  title: t('confirm.clearRegion.title'),
                  body: t('confirm.clearRegion.body'),
                  danger: true,
                }))
              ) {
                return
              }
              onChangePrimitive(blankText())
            })()
          }}
          className="w-full rounded border border-rose-300 px-3 py-1 text-xs text-rose-700 disabled:opacity-40 hover:enabled:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:enabled:bg-rose-950"
        >
          {t('blockConfigurator.regionPanel.clearRegion')}
        </button>
      </section>
    </aside>
  )
}

/** Convert raw bytes to a base64-encoded data URI for display/comparison. */
function bytesToDataUri(bytes: Uint8Array, mimeType: string): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!)
  return `data:${mimeType};base64,${btoa(binary)}`
}

function ImageSourceControls({
  selected,
  onChangePrimitive,
}: {
  selected: ImageComponent
  onChangePrimitive: (next: LeafPrimitive) => void
}) {
  const { t } = useTranslation()
  const adapter = useAdapter()
  const [assets, setAssets] = useState<ImageAsset[]>([])
  useEffect(() => {
    if (!adapter) return
    void adapter.resources.listImageAssets().then(r => {
      if (r.status === 'ok') setAssets(r.data.items)
    })
  }, [adapter])

  const [analysis, setAnalysis] = useState<ImageAnalysis | null>(null)
  // 'field' (runtime data-bound) sources aren't resolvable at author time and the
  // source picker never offers them; they key to '' and analyzeImageSource returns
  // null → the slider degrades to the active `?? 128` state. Intentional, not a gap.
  const sourceKey =
    selected.source.kind === 'data-uri' ? selected.source.value
    : selected.source.kind === 'url' ? selected.source.url
    : selected.source.kind === 'host-asset' ? selected.source.assetId
    : ''
  useEffect(() => {
    let live = true
    setAnalysis(null)
    void analyzeImageSource(selected.source, adapter?.resources).then(a => {
      if (live) setAnalysis(a)
    })
    return () => { live = false }
    // deps: re-run only on real source change (sourceKey) or adapter swap; adapter?.resources is stable per adapter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, sourceKey])

  // Thumbnail src: derive from the current source kind.
  // For host-asset, reconstruct from the stored bytes via bytesToDataUri.
  const thumbnailSrc: string | null = (() => {
    if (selected.source.kind === 'data-uri') {
      return selected.source.value || null
    }
    if (selected.source.kind === 'url') {
      return selected.source.url || null
    }
    if (selected.source.kind === 'host-asset') {
      // Capture the narrowed source outside the closure — TS doesn't carry
      // the `kind === 'host-asset'` narrowing of `selected.source` into the
      // `.find()` callback.
      const source = selected.source
      const asset = assets.find(a => a.id === source.assetId)
      if (asset) return bytesToDataUri(asset.data, asset.mimeType)
      return null
    }
    return null
  })()

  const [thumbError, setThumbError] = useState(false)
  // Reset error when the source changes
  useEffect(() => { setThumbError(false) }, [sourceKey])

  const kind = selected.source.kind
  return (
    <div className="mt-1 space-y-2">
      {/* Thumbnail preview */}
      {thumbnailSrc && !thumbError && (
        <img
          data-testid="rpp-image-thumb"
          src={thumbnailSrc}
          alt={t('blockConfigurator.regionPanel.image.previewAlt')}
          onError={() => setThumbError(true)}
          className="h-12 w-full rounded border border-stone-200 object-contain dark:border-stone-700"
        />
      )}
      {thumbnailSrc && thumbError && (
        <p
          data-testid="rpp-image-thumb-error"
          className="text-[11px] text-red-500 dark:text-red-400"
        >
          {t('blockConfigurator.regionPanel.image.loadError')}
        </p>
      )}

      <select
        data-testid="rpp-image-source-kind"
        value={kind === 'data-uri' ? 'upload' : kind}
        onChange={e => {
          const k = e.target.value
          if (k === 'upload') onChangePrimitive({ ...selected, source: { kind: 'data-uri', value: '' } })
          else if (k === 'url') onChangePrimitive({ ...selected, source: { kind: 'url', url: '' } })
          else onChangePrimitive({ ...selected, source: { kind: 'host-asset', assetId: assets[0]?.id ?? '' } })
        }}
        className="w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
      >
        <option value="upload">{t('blockConfigurator.regionPanel.image.sourceUpload')}</option>
        <option value="url">{t('blockConfigurator.regionPanel.image.sourceUrl')}</option>
        <option value="host-asset">{t('blockConfigurator.regionPanel.image.sourceLibrary')}</option>
      </select>

      {kind === 'data-uri' && (
        <input
          data-testid="rpp-image-upload"
          type="file"
          accept="image/*"
          onChange={e => {
            const file = e.target.files?.[0]
            if (!file) return
            const reader = new FileReader()
            reader.onload = () => {
              const value = String(reader.result)
              // Always update the source (so the canvas reflects immediately)
              onChangePrimitive({ ...selected, source: { kind: 'data-uri', value } })
              if (adapter) {
                // Dedupe: check if an existing asset has identical content
                void adapter.resources.listImageAssets().then(r => {
                  if (r.status !== 'ok') return
                  const duplicate = r.data.items.find(
                    asset => bytesToDataUri(asset.data, asset.mimeType) === value
                  )
                  if (duplicate) return // reuse existing — no new save
                  const base64 = value.split(',')[1] ?? ''
                  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
                  void adapter.resources.saveImageAsset({
                    id: `asset-${Date.now()}`, name: file.name, mimeType: file.type,
                    data: bytes, createdAt: new Date().toISOString(),
                  })
                })
              }
            }
            reader.readAsDataURL(file)
          }}
          className="w-full text-xs"
        />
      )}
      {kind === 'url' && (
        <input
          data-testid="rpp-image-url"
          value={selected.source.kind === 'url' ? selected.source.url : ''}
          onChange={e => onChangePrimitive({ ...selected, source: { kind: 'url', url: e.target.value } })}
          placeholder="https://…/logo.png"
          className="w-full rounded border border-stone-300 bg-white px-2 py-1 font-mono text-xs dark:border-stone-700 dark:bg-stone-950"
        />
      )}
      {kind === 'host-asset' && (
        <select
          data-testid="rpp-image-asset"
          value={selected.source.kind === 'host-asset' ? selected.source.assetId : ''}
          onChange={e => onChangePrimitive({ ...selected, source: { kind: 'host-asset', assetId: e.target.value } })}
          className="w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
        >
          {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      )}

      <label className="block text-xs">
        <span className="text-stone-500">{t('blockConfigurator.regionPanel.image.fit')}</span>
        <select
          data-testid="rpp-image-fit"
          value={selected.fit ?? 'contain'}
          onChange={e => onChangePrimitive({ ...selected, fit: e.target.value as ImageComponent['fit'] })}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
        >
          <option value="contain">{t('blockConfigurator.regionPanel.image.fitContain')}</option>
          <option value="cover">{t('blockConfigurator.regionPanel.image.fitCover')}</option>
          <option value="stretch">{t('blockConfigurator.regionPanel.image.fitStretch')}</option>
        </select>
      </label>

      <ThresholdControl selected={selected} analysis={analysis} onChangePrimitive={onChangePrimitive} />
    </div>
  )
}

function FontSizeInput({
  selected,
  onChangePrimitive,
}: {
  selected: TextComponent
  onChangePrimitive: (next: LeafPrimitive) => void
}) {
  // No explicit sizePt → pre-fill with the default the pipeline actually
  // applies (same pattern as the barcode size placeholders, but as the VALUE
  // since this default always takes effect). Committed semantics preserved:
  // only an explicit user commit of a NON-default value writes sizePt.
  const display = (c: TextComponent) =>
    String(c.sizePt !== undefined ? c.sizePt : DEFAULT_TEXT_SIZE_PT)
  const [draft, setDraft] = useState(display(selected))

  useEffect(() => {
    setDraft(display(selected))
    // `display` reads only sizePt — re-sync exactly when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.sizePt])

  const commit = (raw: string) => {
    if (raw === '') {
      // Explicit clear → back to the default; re-fill the field with it
      // (the prop may already be undefined, so the effect won't fire).
      // Skip the write when sizePt is already unset — it would only dirty
      // the draft with a no-op.
      if (selected.sizePt !== undefined) {
        onChangePrimitive({ ...selected, sizePt: undefined })
      }
      setDraft(String(DEFAULT_TEXT_SIZE_PT))
      return
    }
    const n = Number(raw)
    if (Number.isNaN(n)) return
    const clamped = Math.max(4, n)
    if (clamped === (selected.sizePt ?? DEFAULT_TEXT_SIZE_PT)) {
      // Nothing effectively changed (blur on the displayed value, or the
      // default re-typed while unset) — keep the doc sparse, just normalize.
      setDraft(String(clamped))
      return
    }
    onChangePrimitive({ ...selected, sizePt: clamped })
  }

  return (
    <input
      data-testid="rpp-text-size"
      type="number"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          commit(draft)
          e.currentTarget.blur()
        }
      }}
      className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
    />
  )
}

function BarcodeSizeSection({
  selected,
  onChangePrimitive,
}: {
  selected: SymbolComponent
  onChangePrimitive: (next: LeafPrimitive) => void
}) {
  // Hook before the early return — hook order must not depend on the symbology.
  const { t } = useTranslation()
  if (!(RENDERABLE_SYMBOLOGIES as readonly string[]).includes(selected.symbology)) return null
  const metrics = SYMBOLOGY_METRICS[selected.symbology as RenderableSymbology]
  const isQr = selected.symbology === 'qr'
  return (
    <section className="mb-4">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
        {t('blockConfigurator.regionPanel.size')}
      </label>
      <label className="mt-1 block text-xs">
        <span className="text-stone-500">
          {isQr
            ? t('blockConfigurator.regionPanel.moduleSize')
            : t('blockConfigurator.regionPanel.barWidth')}
        </span>
        <BarcodeSizeField
          testId="rpp-symbol-module-width"
          step="0.01" min={metrics.minModuleWidthMm}
          value={selected.moduleWidthMm}
          appliedDefault={metrics.defaultModuleWidthMm}
          clamp={n => Math.max(metrics.minModuleWidthMm, n)}
          onCommitValue={v => onChangePrimitive({ ...selected, moduleWidthMm: v })}
        />
      </label>
      <p data-testid="rpp-symbol-size-min-note" className="mt-1 text-[11px] text-stone-400">
        {t('blockConfigurator.regionPanel.minModule', { min: metrics.minModuleWidthMm })}
      </p>
      {!isQr && (
        <label className="mt-2 block text-xs">
          <span className="text-stone-500">
            {t('blockConfigurator.regionPanel.barcodeHeight')}
          </span>
          <BarcodeSizeField
            testId="rpp-symbol-height"
            step="0.5" min={1}
            value={selected.heightMm}
            appliedDefault={metrics.defaultHeightMm}
            clamp={n => Math.max(1, n)}
            onCommitValue={v => onChangePrimitive({ ...selected, heightMm: v })}
          />
        </label>
      )}
    </section>
  )
}

/**
 * Numeric size field that displays the APPLIED value: the explicit component
 * value when set, otherwise the SYMBOLOGY_METRICS default the pipeline
 * actually applies (issue #4: freshly dropped barcodes showed empty fields).
 * Same pattern as `FontSizeInput`: the default is shown as the VALUE, but
 * commit semantics stay sparse — only a non-default value (or an explicit
 * clear of a previously set value) writes to the doc. Blur/Enter commit,
 * clamp at commit (consistent with ruler + font size).
 */
function BarcodeSizeField({
  testId,
  step,
  min,
  value,
  appliedDefault,
  clamp,
  onCommitValue,
}: {
  testId: string
  step: string
  min: number
  value: number | undefined
  appliedDefault: number
  clamp: (n: number) => number
  onCommitValue: (next: number | undefined) => void
}) {
  const display = String(value !== undefined ? value : appliedDefault)
  const [draft, setDraft] = useState(display)

  useEffect(() => {
    setDraft(display)
  }, [display])

  const commit = (raw: string) => {
    if (raw === '') {
      // Explicit clear → back to the default; re-fill the field with it (the
      // prop may already be undefined, so the effect won't fire). Skip the
      // write when the value is already unset — it would be a no-op.
      if (value !== undefined) onCommitValue(undefined)
      setDraft(String(appliedDefault))
      return
    }
    const n = Number(raw)
    if (Number.isNaN(n)) return
    const clamped = clamp(n)
    if (clamped === (value ?? appliedDefault)) {
      // Nothing effectively changed (blur on the displayed value, or the
      // default re-typed while unset) — keep the doc sparse, just normalize.
      setDraft(String(clamped))
      return
    }
    onCommitValue(clamped)
  }

  return (
    <input
      data-testid={testId}
      type="number"
      step={step}
      min={min}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          commit(draft)
          e.currentTarget.blur()
        }
      }}
      className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
    />
  )
}

/**
 * Per-symbology humanReadable defaults — single-sourced from
 * SYMBOLOGY_METRICS (contracts), which the pipeline planners also read.
 * Non-renderable symbologies fall back to false.
 */
function defaultHumanReadable(symbology: SymbolComponent['symbology']): boolean {
  if (!(RENDERABLE_SYMBOLOGIES as readonly string[]).includes(symbology)) return false
  return SYMBOLOGY_METRICS[symbology as RenderableSymbology].defaultHumanReadable
}

function HumanReadableSection({
  selected,
  onChangePrimitive,
}: {
  selected: SymbolComponent
  onChangePrimitive: (next: LeafPrimitive) => void
}) {
  const { t } = useTranslation()
  const checked = selected.humanReadable ?? defaultHumanReadable(selected.symbology)
  // Only code-128 / gs1-128 render the pipeline's own sized HRI line; EAN-13
  // keeps the native ^BE interpretation line (its size follows the symbol),
  // so the size input is hidden there.
  const sizable = selected.symbology === 'code-128' || selected.symbology === 'gs1-128'
  return (
    <section className="mb-4">
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          data-testid="rpp-symbol-hri"
          type="checkbox"
          checked={checked}
          onChange={e => onChangePrimitive({ ...selected, humanReadable: e.target.checked })}
          className="h-4 w-4 rounded border-stone-300"
        />
        <span className="text-stone-700 dark:text-stone-300">
          {t('blockConfigurator.regionPanel.humanReadable')}
        </span>
      </label>
      {checked && sizable && (
        <label className="mt-2 block text-xs">
          <span className="text-stone-500">{t('blockConfigurator.regionPanel.hriSize')}</span>
          <BarcodeSizeField
            testId="rpp-symbol-hri-size"
            step="0.5"
            min={4}
            value={selected.humanReadableSizePt}
            appliedDefault={DEFAULT_HRI_SIZE_PT}
            clamp={n => Math.max(4, n)}
            onCommitValue={v => onChangePrimitive({ ...selected, humanReadableSizePt: v })}
          />
        </label>
      )}
    </section>
  )
}

function TextRotationSection({
  selected,
  onChangePrimitive,
}: {
  selected: TextComponent
  onChangePrimitive: (next: LeafPrimitive) => void
}) {
  const { t } = useTranslation()
  return (
    <section className="mb-4">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
        {t('blockConfigurator.regionPanel.rotation')}
      </label>
      <select
        data-testid="rpp-text-rotation"
        value={selected.rotation ?? 0}
        onChange={e => {
          const deg = Number(e.target.value)
          onChangePrimitive({
            ...selected,
            rotation: deg === 0 ? undefined : (deg as TextComponent['rotation']),
          })
        }}
        className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm dark:border-stone-700 dark:bg-stone-950"
      >
        <option value={0}>0°</option>
        <option value={90}>90°</option>
        <option value={180}>180°</option>
        <option value={270}>270°</option>
      </select>
    </section>
  )
}

function ThresholdControl({
  selected,
  analysis,
  onChangePrimitive,
}: {
  selected: ImageComponent
  analysis: ImageAnalysis | null
  onChangePrimitive: (next: LeafPrimitive) => void
}) {
  const { t } = useTranslation()
  const isManual = selected.threshold !== undefined
  const isLocked = analysis?.isMonochrome === true && !isManual
  const displayValue = isManual ? selected.threshold! : (analysis?.otsuThreshold ?? 128)
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-stone-500">
          {!isManual && !isLocked
            ? t('blockConfigurator.regionPanel.threshold.labelAuto', { value: displayValue })
            : t('blockConfigurator.regionPanel.threshold.label', { value: displayValue })}
        </span>
        {isLocked && (
          <button
            type="button"
            data-testid="rpp-image-threshold-adjust"
            onClick={() => onChangePrimitive({ ...selected, threshold: displayValue })}
            className="text-xs text-sky-600 hover:underline"
          >
            {t('blockConfigurator.regionPanel.threshold.adjust')}
          </button>
        )}
        {isManual && (
          <button
            type="button"
            data-testid="rpp-image-threshold-reset"
            onClick={() => onChangePrimitive({ ...selected, threshold: undefined })}
            className="text-xs text-sky-600 hover:underline"
          >
            {t('blockConfigurator.regionPanel.threshold.reset')}
          </button>
        )}
      </div>
      <input
        data-testid="rpp-image-threshold"
        type="range"
        min={0}
        max={255}
        value={displayValue}
        disabled={isLocked}
        aria-describedby={isLocked ? 'rpp-threshold-note' : undefined}
        onChange={e => onChangePrimitive({ ...selected, threshold: Number(e.target.value) })}
        className="mt-1 w-full disabled:opacity-50"
      />
      {isLocked && (
        <p id="rpp-threshold-note" data-testid="rpp-image-threshold-note" className="text-[11px] text-stone-400">
          {t('blockConfigurator.regionPanel.threshold.monochrome')}
        </p>
      )}
    </div>
  )
}
